import ReactMarkdown from "react-markdown";
import CHGReviewResult from "./CHGReviewResult";
import React, { useState, useRef, useEffect } from 'react';
import { FileSearch, Sparkles, TrendingUp, CheckCircle, AlertCircle, Clock, Brain, Award, Target, Minimize2 } from 'lucide-react';

const CHGReview = () => {
  const [changeNumber, setChangeNumber] = useState('CHG0100837');
  const [isReviewing, setIsReviewing] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const [currentScore, setCurrentScore] = useState(0);
  const [currentGrade, setCurrentGrade] = useState('F');
  const [reviewData, setReviewData] = useState(null);
  const wsRef = useRef(null);
  const thinkingBufferRef = useRef('');
  const typingIntervalRef = useRef(null);
  const thinkingEndRef = useRef(null);
  const isConnectingRef = useRef(false);
  const hasCompletedRef = useRef(false);

  const WS_URL = process.env.REACT_APP_WS_URL || 'wss://1r5x85vspl.execute-api.us-west-2.amazonaws.com/production';

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (isConnectingRef.current) {
        reject(new Error('Already connecting'));
        return;
      }

      isConnectingRef.current = true;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        isConnectingRef.current = false;
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Message:', data.type);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('❌ Parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        isConnectingRef.current = false;
        reject(error);
      };

      ws.onclose = () => {
        console.log('🔌 WebSocket closed');
        isConnectingRef.current = false;
      };
    });
  };

  const handleWebSocketMessage = (data) => {
    // Ignore all messages if already completed
    if (hasCompletedRef.current && data.type !== 'error') {
      console.log('Ignoring message after completion:', data.type);
      return;
    }

    switch (data.type) {
      case 'thinking':
        addThinkingText('💭 ' + data.content);
        break;
      case 'agent_thinking':
        const text = data.content;
        addThinkingText(text);
        // Extract score from text like "5/5 points" - only count the first number
        const scoreMatch = text.match(/(\d+)\/(\d+)\s*points?/i);
        if (scoreMatch) {
          const points = parseInt(scoreMatch[1]);
          if (points > 0 && points <= 5) {
            updateScore(points);
          }
        }
        break;
      case 'analysis_start':
        addThinkingText('🚀 ' + data.content);
        break;
      case 'complete':
        if (hasCompletedRef.current) {
          console.log('Already completed, ignoring duplicate complete message');
          return;
        }
        hasCompletedRef.current = true;
        addThinkingText('\n\n✅ ' + (data.message || 'Analysis complete!'));
        
        // Close WebSocket immediately
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        
        if (data.review) {
          setReviewData(data.review);
          const finalScore = data.review.assessment?.risk_benefit_score || 0;
          setCurrentScore(finalScore);
          setCurrentGrade(data.review.assessment?.risk_benefit_grade || 'C');
          setIsReviewing(false);
          
          setTimeout(() => {
            setReviewComplete(true);
            setShowSidePanel(false);
          }, 1500);
        }
        break;
      case 'error':
        addThinkingText('\n\n❌ ERROR: ' + data.content);
        setIsReviewing(false);
        hasCompletedRef.current = true; // Prevent further processing
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        break;
      default:
        break;
    }
  };

  const updateScore = (points) => {
    setCurrentScore(prev => {
      const newScore = Math.min(prev + points, 100);
      // Update grade based on score
      if (newScore >= 90) setCurrentGrade('A');
      else if (newScore >= 80) setCurrentGrade('B');
      else if (newScore >= 70) setCurrentGrade('C');
      else if (newScore >= 60) setCurrentGrade('D');
      else setCurrentGrade('F');
      return newScore;
    });
  };

  const addThinkingText = (text) => {
    if (thinkingBufferRef.current && text && !text.startsWith('\n') && !thinkingBufferRef.current.endsWith('\n')) {
      thinkingBufferRef.current += '\n';
    }
    thinkingBufferRef.current += text;
    if (!typingIntervalRef.current) {
      startTyping();
    }
  };

  const startTyping = () => {
    typingIntervalRef.current = setInterval(() => {
      setThinkingText(prev => {
        if (prev.length < thinkingBufferRef.current.length) {
          // Variable speed: randomly slow down for readability
          const shouldSlowDown = Math.random() < 0.15; // 15% chance to pause
          if (shouldSlowDown) {
            return prev; // Pause for this interval
          }
          
          // Random chunk size between 1-3 characters
          const chunk = Math.floor(Math.random() * 3) + 1;
          const newLength = Math.min(prev.length + chunk, thinkingBufferRef.current.length);
          
          // Auto-scroll to bottom
          if (thinkingEndRef.current) {
            thinkingEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
          
          return thinkingBufferRef.current.substring(0, newLength);
        } else {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          return prev;
        }
      });
    }, 50);
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!changeNumber.trim()) {
      alert('Please enter a change number');
      return;
    }

    // Prevent multiple simultaneous reviews
    if (isReviewing || isConnectingRef.current) {
      console.log('Review already in progress');
      return;
    }

    // Close existing WebSocket if open
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear any existing typing interval
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }

    // Reset flags
    hasCompletedRef.current = false;
    isConnectingRef.current = false;

    setIsReviewing(true);
    setReviewComplete(false);
    setShowSidePanel(true);
    setThinkingText('');
    setReviewData(null);
    setCurrentScore(0);
    setCurrentGrade('F');
    thinkingBufferRef.current = '';

    try {
      await connectWebSocket();
      wsRef.current.send(JSON.stringify({
        action: 'review_change',
        change_number: changeNumber,
        force_refresh: true
      }));
      console.log('📤 Review request sent for', changeNumber);
    } catch (error) {
      alert('Failed to connect. Please try again.');
      console.error('❌ Connection error:', error);
      setIsReviewing(false);
      setShowSidePanel(false);
    }
  };

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A': return { bg: 'bg-blue-500', text: 'text-blue-400', shadow: 'rgba(57, 255, 20, 0.6)' };
      case 'B': return { bg: 'bg-blue-500', text: 'text-blue-400', shadow: 'rgba(0, 229, 255, 0.6)' };
      case 'C': return { bg: 'bg-yellow-accent', text: 'text-text-primary', shadow: 'rgba(255, 255, 0, 0.6)' };
      case 'D': return { bg: 'bg-blue-500', text: 'text-blue-400', shadow: 'rgba(255, 107, 53, 0.6)' };
      default: return { bg: 'bg-gray-500', text: 'text-text-secondary', shadow: 'rgba(128, 128, 128, 0.6)' };
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 relative min-h-full">
      {/* Neon glow effects */}
                  
      {/* Header */}
      <div className="mb-6 relative z-10">
        <div className="flex items-center space-x-3 mb-2">
          <Sparkles className="text-blue-400" size={28} strokeWidth={2.5} 
                      />
          <h2 className="text-xl sm:text-2xl font-bold text-blue-400 tracking-tight">
            CHG Review
          </h2>
        </div>
        <p className="text-xs sm:text-sm text-text-secondary">AI-powered change request analysis and policy compliance review</p>
      </div>

      {/* Side Panel - Hidden in v2, replaced by inline display */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[90%] md:w-[500px] lg:w-[600px] bg-surface border-l-2 border-purple-accent z-50 transform transition-transform duration-300 ${
        'translate-x-full' /* always hidden in v2 */
      }`}
           >
        
        {/* Header */}
        <div className="sticky top-0 bg-surface border-b border-purple-accent/30 p-3 sm:p-4 flex items-center justify-between z-10"
             >
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Brain className="text-purple-accent animate-pulse" size={20} strokeWidth={2.5}
                    />
            <div>
              <h3 className="text-base sm:text-lg font-bold text-purple-accent">
                AI Analysis
              </h3>
              <p className="text-xs text-text-muted">v2.4.0</p>
            </div>
          </div>
          <button
            onClick={() => setShowSidePanel(false)}
            className="text-text-secondary hover:text-purple-accent transition-colors p-2"
            title="Minimize">
            <Minimize2 size={18} />
          </button>
        </div>

        {/* Live Score Display */}
        <div className="p-3 sm:p-4 border-b border-border/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* Grade Badge */}
              <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl ${getGradeColor(currentGrade).bg} flex items-center justify-center transition-all duration-500`}
                   style={{ boxShadow: `0 0 30px ${getGradeColor(currentGrade).shadow}` }}>
                <span className="text-2xl sm:text-3xl font-bold text-black">{currentGrade}</span>
              </div>
              
              {/* Score */}
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-text-primary transition-all duration-500"
                     >
                  {currentScore}
                </div>
                <div className="text-xs text-text-secondary">Current Score</div>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="relative w-full h-2 bg-dark-border rounded-full overflow-hidden">
            <div className={`absolute inset-y-0 left-0 ${getGradeColor(currentGrade).bg} transition-all duration-500 rounded-full`}
                 style={{ 
                   width: `${currentScore}%`,
                   boxShadow: `0 0 15px ${getGradeColor(currentGrade).shadow}`
                 }} />
          </div>
        </div>

        {/* Thinking Content */}
        <div className="p-3 sm:p-4 h-[calc(100vh-200px)] overflow-y-auto">
          <div className="font-mono text-xs sm:text-sm text-text-primary leading-relaxed" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {thinkingText}
            {isReviewing && (
              <span className="inline-block w-2 h-3 bg-purple-accent ml-1 animate-pulse" />
            )}
            <div ref={thinkingEndRef} />
          </div>
        </div>
      </div>

      {/* Floating Maximize Button */}
      {false && (
        <button
          onClick={() => setShowSidePanel(true)}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 p-3 sm:p-4 bg-purple-accent text-black rounded-full shadow-lg hover:scale-110 transition-all"
          title="Show AI Analysis">
          <Brain size={20} strokeWidth={2.5} className="sm:w-6 sm:h-6" />
        </button>
      )}

      {/* Inline Review Results */}
      {(thinkingText || reviewComplete) && (
        <div className="mb-6 relative z-10">
          {/* Thinking/Progress Messages */}
          {isReviewing && thinkingText && (
            <div className="mb-4 p-4 bg-surface border border-purple-accent/30 rounded-card">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-purple-accent animate-pulse" />
                <span className="text-xs text-purple-accent font-semibold">AI Processing</span>
              </div>
              <div className="font-mono text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">
                {thinkingText}
                <span className="inline-block w-2 h-3 bg-purple-accent ml-1 animate-pulse" />
              </div>
            </div>
          )}

          {/* Final Review Card */}
          {reviewComplete && reviewData && (
            <div className="space-y-4">
              {/* Score Header */}
              <div className="flex items-center justify-between p-4 bg-surface border border-border rounded-card">
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    reviewData.assessment?.risk_benefit_grade === 'A' ? 'bg-blue-500' :
                    reviewData.assessment?.risk_benefit_grade === 'B' ? 'bg-yellow-400' : 'bg-red-500'
                  }`}>
                    <span className="text-2xl font-bold text-black">{reviewData.assessment?.risk_benefit_grade || '?'}</span>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-text-primary">{reviewData.assessment?.risk_benefit_score || 0}</div>
                    <div className="text-xs text-text-secondary">Compliance Score</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${
                    reviewData.assessment?.approval_recommendation === 'Approve' ? 'text-blue-400' : 'text-blue-400'
                  }`}>
                    {reviewData.assessment?.approval_recommendation || 'Pending'}
                  </div>
                  <div className="text-xs text-text-secondary">{reviewData.assessment?.compliance_percentage || 0}% Compliant</div>
                </div>
              </div>

              {/* Structured Review */}
              <CHGReviewResult reviewData={reviewData} />

              {/* New Review Button */}
              <button
                onClick={() => { setReviewComplete(false); setReviewData(null); setThinkingText(''); }}
                className="w-full px-4 py-3 bg-surface border border-blue-500/40 text-blue-400 font-semibold text-sm rounded hover:bg-blue-500/10 transition-all"
              >
                New Review
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating Maximize Button */}
      {false && (
        <button
          onClick={() => setShowSidePanel(true)}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 p-3 sm:p-4 bg-purple-accent text-black rounded-full shadow-lg hover:scale-110 transition-all"
          title="Show AI Analysis">
          <Brain size={20} strokeWidth={2.5} className="sm:w-6 sm:h-6" />
        </button>
      )}

      {/* Results Section - Full Page (DEPRECATED — replaced by Final Review Card + CHGReviewResult) */}
      {false && reviewComplete && reviewData && !showSidePanel && (
        <div className="mb-4 sm:mb-6 relative z-10">
          <div className="relative bg-surface border-2 border-blue-500/40 rounded-card p-4 sm:p-6 overflow-hidden"
               style={{
                 
                 }}>
            
            {/* Background decoration */}
            <div className="absolute -right-10 -top-10 opacity-[0.05] text-blue-400 pointer-events-none"
                 >
              <Award size={200} strokeWidth={1} />
            </div>

            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-text-primary flex items-center">
                  <CheckCircle className="text-blue-400 mr-2 sm:mr-3" size={24} strokeWidth={2.5}
                               />
                  <span>
                    Review Complete
                  </span>
                </h3>
                <button
                  onClick={() => setReviewComplete(false)}
                  className="text-text-secondary hover:text-text-primary transition-colors text-sm"
                >
                  ✕
                </button>
              </div>

              {/* Score Display */}
              <div className="flex flex-col items-center justify-center mb-6 sm:mb-8">
                {/* Grade Badge */}
                <div className="relative mb-3 sm:mb-4">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-blue-500 flex items-center justify-center relative">
                    <span className="text-3xl sm:text-4xl font-bold text-black">
                      {reviewData.assessment?.risk_benefit_grade || 'A'}
                    </span>
                  </div>
                </div>

                {/* Score Number */}
                <div className="text-5xl sm:text-7xl font-bold text-text-primary mb-2">
                  {reviewData.assessment?.risk_benefit_score || 93}
                </div>
                <p className="text-text-secondary text-sm mb-3 sm:mb-4">Current Score</p>

                {/* Status Badge */}
                <div className="inline-block px-6 py-2 bg-blue-500/15 border border-blue-500/40 rounded-full">
                  <span className="text-blue-400 font-semibold text-sm">
                    {reviewData.assessment?.approval_recommendation === 'Approve' ? 'Recommended for Approval' : 'Request More Information'}
                  </span>
                </div>
              </div>

              {/* Policy Compliance */}
              <div className="mb-6 p-5 bg-bg/50 border border-blue-500/30 rounded-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-text-secondary text-sm">Policy Compliance</span>
                  <span className="text-blue-400 font-bold text-sm">
                    {reviewData.assessment?.overall_policy_compliance?.replace('_', ' ') || 'Partially Compliant'}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="relative w-full h-3 bg-dark-border rounded-full overflow-hidden"
                     >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-400 to-blue-300 rounded-full"
                       style={{
                         width: `${reviewData.assessment?.compliance_percentage || 93}%`,
                         background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 50%, #93c5fd 100%)'
                       }} />
                </div>
                <p className="text-center text-text-secondary text-xs mt-2">
                  {reviewData.assessment?.compliance_percentage || 93}% Compliant
                </p>
              </div>

              {/* Score Improvement Potential */}
              {reviewData.score_improvement && (
                <div className="mb-6">
                  <h4 className="text-text-primary font-bold mb-3 flex items-center">
                    <Target className="text-blue-400 mr-2" size={20}
                             />
                    <span>
                      Score Improvement Potential
                    </span>
                  </h4>
                  
                  <div className="flex items-center gap-3">
                    {/* Current Score Bar */}
                    <div className="flex-1 relative h-16 bg-dark-border rounded-card overflow-hidden"
                         >
                      <div className="absolute inset-0 bg-blue-500 rounded-card flex items-center justify-center"
                           style={{
                             width: `${reviewData.score_improvement.current_score}%`,
                             }}>
                        <span className="text-2xl font-bold text-black">
                          {reviewData.score_improvement.current_score}
                        </span>
                      </div>
                    </div>

                    {/* Max Score */}
                    <div className="w-20 h-16 bg-dark-border/50 border-2 border-blue-500/40 rounded-card flex items-center justify-center">
                      <span className="text-2xl font-bold text-blue-400">
                        {reviewData.score_improvement.potential_score}
                      </span>
                    </div>
                  </div>
                  <p className="text-center text-text-secondary text-xs mt-2">
                    {reviewData.score_improvement.point_gain} points available for improvement
                  </p>
                </div>
              )}

              {/* Policy Compliance Details */}
              {reviewData.policy_compliance && (
                <div className="mb-6">
                  {/* Compliant Requirements */}
                  {reviewData.policy_compliance.compliant_requirements?.length > 0 && (
                    <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-card">
                      <h4 className="text-blue-400 font-bold mb-3 flex items-center">
                        <CheckCircle size={18} className="mr-2" />
                        Requirements Met ({reviewData.policy_compliance.compliant_requirements.length})
                      </h4>
                      <ul className="space-y-2">
                        {reviewData.policy_compliance.compliant_requirements.slice(0, 5).map((req, idx) => (
                          <li key={idx} className="text-xs text-text-primary flex items-start">
                            <span className="text-blue-400 mr-2">✓</span>
                            <span>{req.requirement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Non-Compliant Requirements */}
                  {reviewData.policy_compliance.non_compliant_requirements?.length > 0 && (
                    <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-card">
                      <h4 className="text-blue-400 font-bold mb-3 flex items-center">
                        <AlertCircle size={18} className="mr-2" />
                        Requirements Missing ({reviewData.policy_compliance.non_compliant_requirements.length})
                      </h4>
                      <ul className="space-y-2">
                        {reviewData.policy_compliance.non_compliant_requirements.slice(0, 5).map((req, idx) => (
                          <li key={idx} className="text-xs text-text-primary flex items-start">
                            <span className="text-blue-400 mr-2">✗</span>
                            <span>{req.requirement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                <button
                  onClick={() => setShowSidePanel(true)}
                  className="px-4 py-3 bg-purple-accent/15 border border-purple-accent/40 rounded text-purple-accent font-semibold text-sm hover:bg-purple-accent/25 transition-all">
                  <Brain className="inline mr-2" size={16} />
                  View AI Analysis
                </button>
                <button
                  onClick={() => {
                    setReviewComplete(false);
                    setReviewData(null);
                    setCurrentScore(0);
                    setCurrentGrade('F');
                    setThinkingText('');
                    thinkingBufferRef.current = '';
                    hasCompletedRef.current = false;
                  }}
                  className="px-4 py-3 bg-blue-500/15 border border-blue-500/40 rounded text-blue-400 font-semibold text-sm hover:bg-blue-500/25 transition-all">
                  <FileSearch className="inline mr-2" size={16} />
                  New Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid - Hide when review is complete */}
      {!reviewComplete && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 relative z-10">
        
        {/* Information Box */}
        <div className="relative bg-surface border border-border rounded-card p-5 overflow-hidden group"
             >
          
          {/* Background Icon */}
          <div className="absolute -right-6 -top-6 opacity-[0.08] text-blue-400 pointer-events-none"
               >
            <FileSearch size={120} strokeWidth={1.5} />
          </div>

          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-blue-400 mb-3">
              Change Request Pre-Review Tool
            </h3>
            
            <p className="text-sm text-text-secondary mb-5 leading-relaxed">
              Enter your change request number to have it automatically reviewed against our change management 
              policy standards. This tool helps you identify gaps and strengthen your request before formal submission.
            </p>

            {/* What You'll Get */}
            <div className="mb-5">
              <h4 className="text-sm font-bold text-purple-accent mb-3 flex items-center">
                <TrendingUp size={16} className="mr-2" />
                What You'll Get:
              </h4>
              <ul className="space-y-2.5">
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span><span className="text-blue-400 font-semibold">Quality Score</span> - See how your change request measures up (0-100 scale)</span>
                </li>
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span><span className="text-blue-400 font-semibold">Priority Recommendations</span> - Ranked list of improvements that will have the biggest impact</span>
                </li>
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span><span className="text-blue-400 font-semibold">Detailed Guidance</span> - Specific examples of what information to add</span>
                </li>
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span><span className="text-blue-400 font-semibold">Score Improvement Potential</span> - Know exactly how many points you can gain by addressing each area</span>
                </li>
              </ul>
            </div>

            {/* Why Use This Tool */}
            <div>
              <h4 className="text-sm font-bold text-purple-accent mb-3 flex items-center">
                <CheckCircle size={16} className="mr-2" />
                Why Use This Tool:
              </h4>
              <ul className="space-y-2.5">
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Catch issues early before your change is rejected or sent back for revision</span>
                </li>
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Reduce approval delays by submitting complete requests the first time</span>
                </li>
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Learn what reviewers look for in high-quality change requests</span>
                </li>
                <li className="flex items-start text-xs sm:text-sm text-text-secondary">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0" />
                  <span>Improve your change management skills with actionable feedback</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Neon glow border effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none text-blue-400 rounded-card"
                />
        </div>

        {/* Review Form Box */}
        <div className="relative bg-surface border border-border rounded-card p-5 overflow-hidden group"
             >
          
          {/* Background Icon */}
          <div className="absolute -right-6 -top-6 opacity-[0.08] text-blue-400 pointer-events-none"
               >
            <Sparkles size={120} strokeWidth={1.5} />
          </div>

          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-blue-400 mb-2">
              Submit for Review
            </h3>
            
            <p className="text-sm text-text-secondary mb-6">
              Enter your change request number below to begin the automated review process.
            </p>

            {/* Form */}
            <form onSubmit={handleReview} className="space-y-4">
              <div>
                <label htmlFor="changeNumber" className="block text-xs font-semibold text-text-secondary mb-2 uppercase tracking-wider">
                  Change Request Number
                </label>
                <input
                  type="text"
                  id="changeNumber"
                  value={changeNumber}
                  onChange={(e) => setChangeNumber(e.target.value)}
                  placeholder="e.g., CHG0012345"
                  className="w-full px-4 py-3 bg-surface-2 border border-border rounded text-text-primary text-sm placeholder-text-muted focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  
                  disabled={isReviewing}
                />
              </div>

              <button
                type="submit"
                disabled={!changeNumber.trim() || isReviewing}
                className="w-full px-6 py-3 bg-blue-500 text-text-primary font-semibold text-sm rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600 relative overflow-hidden group"
              >
                {isReviewing ? (
                  <span className="flex items-center justify-center">
                    <Clock className="animate-spin mr-2" size={18} />
                    Reviewing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FileSearch className="mr-2" size={18} />
                    Start Review
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity transform -skew-x-12" />
              </button>
            </form>

            {/* Info Cards */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-bg/50 border border-border/50 rounded p-3">
                <div className="flex items-center mb-1">
                  <CheckCircle size={16} className="text-blue-400 mr-2" 
                               />
                  <span className="text-xs font-bold text-blue-400">
                    Fast Analysis
                  </span>
                </div>
                <p className="text-xs text-text-secondary">Results in under 30 seconds</p>
              </div>

              <div className="bg-bg/50 border border-border/50 rounded p-3">
                <div className="flex items-center mb-1">
                  <Sparkles size={16} className="text-purple-accent mr-2"
                            />
                  <span className="text-xs font-bold text-purple-accent">
                    AI-Powered
                  </span>
                </div>
                <p className="text-xs text-text-secondary">Advanced policy analysis</p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-6 p-3 bg-bg/30 border border-yellow-accent/20 rounded">
              <div className="flex items-start">
                <AlertCircle size={16} className="text-text-primary mr-2 mt-0.5 flex-shrink-0"
                             />
                <p className="text-xs text-text-secondary">
                  This is a pre-review tool. Your change request will still need to go through the formal approval process.
                </p>
              </div>
            </div>
          </div>

          {/* Neon glow border effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none text-blue-400 rounded-card"
                />
        </div>
      </div>
      )}
    </div>
  );
};

export default CHGReview;

